import { RefObject, useMemo } from "react"
import { Device } from "../../../corp/devices/types"
import { Medication } from "../../../corp/medications/types"
import { Kit, KitsByStatus } from "./types"
import useCustomerNumber from "../../../../hooks/useCustomerNumber"
import { useReactToPrint } from "react-to-print"
import { getKitImage, getKitItemsById, getOrderedKits } from "./utils/helpers"
import { Box, Button } from "@mui/material"
import PrintIcon from "@mui/icons-material/Print"
import KitContainer from "./KitContainer"
import { medicationIconBlue } from "../../../../icons"
import { KitType } from "./enums"

type TabContent = {
  type: KitType,
  items: (Device | Medication)[],
  header: string,
  kitsByStatus: KitsByStatus,
  tabIndex: number,
  contentRef: RefObject<HTMLDivElement>,
  fetchItems: (customerNumber: string) => Promise<void>,
}

const TabContent = (props: TabContent) => {
  const {
    type,
    items,
    header,
    kitsByStatus,
    tabIndex,
    contentRef,
    fetchItems,
  } = props

  const customerNumber = useCustomerNumber()
  const reactToPrintFn = useReactToPrint({ contentRef })

  const kitItemsById = useMemo(() => {
    return getKitItemsById(items)
  }, [items])  

  const sortedKits = useMemo(() => {
    const key = tabIndex === 0 ? 'active' : 'inactive'
    const kits = kitsByStatus[key]

    return getOrderedKits(kits)
  }, [kitsByStatus, tabIndex])

  const defaultItemIcon = type === KitType.Device
    ? getKitImage('no_image') || ''
    : medicationIconBlue

  return (
    <Box sx={{ p: '0 16px 16px 16px' }}>
      <Box className="flex flex-space-between m-b-16">
        <Box className="page-header-name">
          { header }
        </Box>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={reactToPrintFn}
          className="print-hide"
          disabled={sortedKits.length === 0}
        >
          Print {tabIndex === 0 ? 'Active' : 'Inactive'} Kits
        </Button>
      </Box>
      {
        sortedKits.map((kit: Kit) => (
          <KitContainer
            key={kit.id}
            kit={kit}
            kitItemsById={kitItemsById}
            type={type}
            customerNumber={customerNumber}
            defaultKitIcon="default-kit-new"
            defaultItemIcon={defaultItemIcon}
            fetchItems={fetchItems}
          />
        ))
      }
      {
        sortedKits.length === 0 && type === KitType.Device && (
          <Box>No {tabIndex === 0 ? 'Active' : 'Inactive'} AEDs or devices.</Box>
        )
      }
    </Box>
  )
}

export default TabContent
